import { Check, Copy } from 'lucide-react'
import type { ComponentPropsWithoutRef } from 'react'
import type { ExtraProps } from 'react-markdown'
import { useCopy } from '@/hooks/useCopy'
import { MermaidDiagram } from './MermaidDiagram'

type HastLike = {
  type: string
  value?: string
  tagName?: string
  properties?: Record<string, unknown>
  children?: HastLike[]
}

function hastText(node: HastLike | undefined): string {
  if (!node) return ''
  if (node.type === 'text') return node.value ?? ''
  return (node.children ?? []).map(hastText).join('')
}

function languageOf(code: HastLike | undefined): string | null {
  const className = code?.properties?.className
  const classes = Array.isArray(className) ? className.map(String) : []
  const lang = classes.find((c) => c.startsWith('language-'))
  return lang ? lang.slice('language-'.length) : null
}

/** Fenced code block with language label and copy button (replaces <pre>); ```mermaid is drawn as a diagram. */
export function CodeBlock({ node, ...props }: ComponentPropsWithoutRef<'pre'> & ExtraProps) {
  const code = (node?.children as HastLike[] | undefined)?.find(
    (child) => child.type === 'element' && child.tagName === 'code',
  )
  const language = languageOf(code)
  if (language === 'mermaid') return <MermaidDiagram source={hastText(code)} />
  return <PlainCodeBlock code={code} language={language} {...props} />
}

function PlainCodeBlock({
  code,
  language,
  children,
  ...props
}: ComponentPropsWithoutRef<'pre'> & { code: HastLike | undefined; language: string | null }) {
  const { copied, copy } = useCopy()

  return (
    <div className="my-4 overflow-hidden rounded-lg border border-line bg-code">
      <div className="flex h-9 items-center justify-between border-b border-line pl-3 pr-1.5 text-xs text-ink-faint">
        <span className="font-mono">{language ?? 'text'}</span>
        <button
          type="button"
          onClick={() => void copy(hastText(code))}
          className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 font-medium text-ink-muted transition-colors hover:bg-subtle hover:text-ink"
          aria-label={copied ? 'Copied code' : 'Copy code'}
        >
          {copied ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto p-3.5 font-mono text-[13px] leading-6" {...props}>
        {children}
      </pre>
    </div>
  )
}
